import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  addDays,
  endOfWeek,
  isSameDay,
  startOfDay,
  startOfWeek,
} from "date-fns";
import {
  AlertTriangle,
  Check,
  ChevronDown,
  GanttChartSquare,
  KanbanSquare,
  List,
  Loader2,
  Plus,
  RefreshCcw,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useCurrentProject } from "@/hooks/useCurrentProject";
import { useFeature } from "@/hooks/useFeature";
import { usePermissions } from "@/hooks/usePermissions";
import { useSavedViews } from "@/hooks/useSavedViews";
import { useAuthStore } from "@/stores/auth.store";
import { useTaskStatuses } from "@/hooks/useTaskStatuses";
import { useOpenTask } from "@/hooks/useOpenTask";
import { rememberedControls, withControls } from "@/lib/taskReturn";
import {
  completeTask,
  deleteTask,
  getAllMyTasks,
  listTasks,
  reopenTask,
  updateTask,
} from "@/api/tasks";
import { listMembers } from "@/api/projects";
import { formatNumber } from "@/lib/formatters";
import { cn } from "@/lib/utils";
import type {
  ProjectMember,
  SavedViewRead,
  TaskListParams,
  TaskPriority,
  TaskRead,
  TaskSummary,
} from "@/api/types";
import { MyTasksKanban } from "@/components/tasks/MyTasksKanban";
import { TaskViewSelector } from "@/components/tasks/TaskViewSelector";
import { TimelineView } from "@/components/tasks/TimelineView";
import { TimelineNavigation } from "@/components/tasks/TimelineNavigation";
import { TaskList, TaskListSkeleton } from "@/components/tasks/TaskList";
import { TaskKanbanBoard } from "@/components/tasks/TaskKanbanBoard";
import {
  TaskSummaryCards,
  type SummaryKey,
} from "@/components/tasks/TaskSummaryCards";
import { TaskCreateDialog } from "@/components/tasks/TaskCreateDialog";
import { TaskDetailSheet } from "@/components/tasks/TaskDetailSheet";
import { PRIORITY_RANK } from "@/components/tasks/priority";
import { paletteFor } from "@/components/tasks/statusPalette";

/** Scope toggle — orthogonal to the temporal pills. Always visible,
 *  one of two options selected at any time. */
type Scope = "all" | "my_tasks";

type TaskViewMode = "list" | "kanban" | "timeline";

/** Filter blob persisted inside a saved view's `filters` JSON. */
interface TaskViewFilters {
  tab?: Scope;
  view_mode?: TaskViewMode;
  /** Array for forward-compat with multi-assignee; today single-select
   *  ("all" or a member id in slot 0). */
  filter_user_ids?: string[];
  /** Drives both card filtering (both views) and Kanban column visibility. */
  filter_statuses?: string[];
}

/** What the page's history entry keeps of its controls (Prism task #270):
 *  coming back from a task's company shows the page as it was left. */
interface TasksControls {
  scope: Scope;
  viewMode: TaskViewMode;
  quick: QuickFilter;
  priority: TaskPriority | "all";
  statuses: string[];
  assignee: string;
  viewId: number | null;
}

/** Quick-filter pills are mutually exclusive — only one can be on at
 *  a time. `null` means "no temporal lens", which is also the default
 *  state and the trigger for the list view's implicit "hide done"
 *  behavior. The scope toggle stays orthogonal and composes with
 *  whichever pill is active. */
type QuickFilter =
  | null
  | "overdue"
  | "dueToday"
  | "dueThisWeek"
  | "noDueDate"
  | "completed";

export default function TasksPage() {
  const navigate = useNavigate();
  const { project } = useCurrentProject();
  const perms = usePermissions();
  const currentUser = useAuthStore((s) => s.user);
  const slug = project?.slug ?? null;
  const [searchParams, setSearchParams] = useSearchParams();
  const companyIdParam = searchParams.get("company_id");
  const companyIdFilter = companyIdParam ? Number(companyIdParam) : null;

  // Route guard.
  useEffect(() => {
    if (slug && !perms.canViewTasks) {
      toast.info("You don't have access to this page.");
      navigate(`/projects/${slug}/dashboard`, { replace: true });
    }
  }, [slug, perms.canViewTasks, navigate]);

  // "My tasks" is the default for everyone — it's the cross-project
  // personal pile (every project the user belongs to). The toggle flips
  // to "All", the current project's full board.
  const [scope, setScope] = useState<Scope>("my_tasks");
  function setScopeManual(next: Scope) {
    setScope(next);
  }

  // Mutually-exclusive temporal filter. null = "default" state, in
  // which the list view hides done tasks (kanban does not — done
  // columns stay populated).
  const [activeQuickFilter, setActiveQuickFilter] =
    useState<QuickFilter>(null);
  const [priority, setPriority] = useState<TaskPriority | "all">("all");
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  // Assignee narrowing — "all" or a member id. Only applies in the
  // Project Tasks scope (My Tasks is already the current user's pile).
  const [assigneeFilter, setAssigneeFilter] = useState<string>("all");

  function toggleQuickFilter(filter: NonNullable<QuickFilter>) {
    // Click on the active pill clears it back to null; click on any
    // other pill replaces the previous one. Either way only one pill
    // is ever active at a time.
    setActiveQuickFilter((prev) => (prev === filter ? null : filter));
  }

  // List vs Kanban. When the project's tasks_kanban feature flag is off,
  // the toggle is hidden and the page is forced to list view regardless
  // of any stored preference.
  const kanbanEnabled = useFeature("tasks_kanban");
  // View mode is part of the saved view preference (DB) — seeded once on
  // load via the effect below; session-local until "Save as default".
  const [viewMode, setViewMode] = useState<TaskViewMode>("list");
  // Timeline date window (default: current week → +2 weeks).
  const [timelineStart, setTimelineStart] = useState<Date>(() =>
    startOfWeek(new Date(), { weekStartsOn: 1 }),
  );
  const [timelineEnd, setTimelineEnd] = useState<Date>(() =>
    addDays(startOfWeek(new Date(), { weekStartsOn: 1 }), 13),
  );
  // Force list view when kanban is disabled — guards a stale saved
  // preference from rendering a kanban the operator can't toggle out of.
  useEffect(() => {
    if (!kanbanEnabled) setViewMode("list");
  }, [kanbanEnabled]);

  const [tasks, setTasks] = useState<TaskRead[]>([]);
  const [members, setMembers] = useState<ProjectMember[]>([]);
  // Members of the edited task's project when it isn't the page's (a
  // cross-project row from the My Tasks feed). Empty otherwise — the
  // shared `members` list already covers same-project edits.
  const [editMembers, setEditMembers] = useState<ProjectMember[]>([]);
  const taskStatuses = useTaskStatuses(slug);

  // ─── Saved views (named, per project) ──────────────────────────────────
  const savedViews = useSavedViews("tasks", slug);
  const [activeView, setActiveView] = useState<SavedViewRead | null>(null);

  // Apply a saved view's filter blob to the live controls.
  const applyView = useCallback((view: SavedViewRead | null) => {
    setActiveView(view);
    if (!view) {
      // No view (e.g. switching to a project with no default) — reset to
      // page defaults so one project's filters don't bleed into another.
      setScope("my_tasks");
      setViewMode("list");
      setAssigneeFilter("all");
      setStatusFilter([]);
      return;
    }
    const f = view.filters as TaskViewFilters;
    if (f.tab === "my_tasks" || f.tab === "all") setScope(f.tab);
    if (
      f.view_mode === "list" ||
      f.view_mode === "kanban" ||
      f.view_mode === "timeline"
    ) {
      setViewMode(f.view_mode);
    }
    if (Array.isArray(f.filter_user_ids)) {
      const first = f.filter_user_ids[0];
      setAssigneeFilter(typeof first === "string" ? first : "all");
    }
    if (Array.isArray(f.filter_statuses)) {
      setStatusFilter(f.filter_statuses);
    }
  }, []);

  // The controls this history entry was left with, used once: coming back
  // to the page shows it as it was rather than as its default view.
  const location = useLocation();
  const leftWith = useRef(
    rememberedControls<TasksControls>(location.state, "tasks"),
  );

  // Apply the default view once, after the list settles, per project.
  const seededRef = useRef(false);
  useEffect(() => {
    seededRef.current = false;
  }, [slug]);
  useEffect(() => {
    if (savedViews.loading || seededRef.current) return;
    seededRef.current = true;
    const kept = leftWith.current;
    leftWith.current = null;
    if (kept) {
      setActiveView(savedViews.views.find((v) => v.id === kept.viewId) ?? null);
      if (kept.scope === "my_tasks" || kept.scope === "all") setScope(kept.scope);
      if (
        kept.viewMode === "list" ||
        kept.viewMode === "kanban" ||
        kept.viewMode === "timeline"
      ) {
        setViewMode(kept.viewMode);
      }
      setActiveQuickFilter(kept.quick ?? null);
      setPriority(kept.priority ?? "all");
      setStatusFilter(Array.isArray(kept.statuses) ? kept.statuses : []);
      setAssigneeFilter(typeof kept.assignee === "string" ? kept.assignee : "all");
      return;
    }
    // Apply the default view, or reset to page defaults when there is none
    // (applyView(null) handles the reset) — so each project starts clean.
    applyView(savedViews.defaultView);
  }, [
    slug,
    savedViews.loading,
    savedViews.defaultView,
    savedViews.views,
    applyView,
  ]);

  // Keep the live controls on the history entry, once seeded (before that
  // they are the defaults, not the user's).
  const here = useRef(location);
  here.current = location;
  useEffect(() => {
    if (!seededRef.current) return;
    const controls: TasksControls = {
      scope,
      viewMode,
      quick: activeQuickFilter,
      priority,
      statuses: statusFilter,
      assignee: assigneeFilter,
      viewId: activeView?.id ?? null,
    };
    const { pathname, search, hash, state } = here.current;
    const kept = rememberedControls<TasksControls>(state, "tasks");
    if (JSON.stringify(kept) === JSON.stringify(controls)) return;
    navigate(
      { pathname, search, hash },
      { replace: true, state: withControls(state, "tasks", controls) },
    );
  }, [
    scope,
    viewMode,
    activeQuickFilter,
    priority,
    statusFilter,
    assigneeFilter,
    activeView?.id,
    navigate,
  ]);

  const [createDefaultStatus, setCreateDefaultStatus] = useState<
    string | null
  >(null);
  const [status, setStatus] = useState<"loading" | "ok" | "error">("loading");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTask, setEditTask] = useState<TaskRead | null>(null);
  // Reopened on coming back from the task's company (task #270).
  const [detailTask, setDetailTask] = useOpenTask("tasks", tasks);
  const [deleteTarget, setDeleteTarget] = useState<TaskRead | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [fadingIds, setFadingIds] = useState<Set<number>>(new Set());
  const fadeTimers = useRef<Map<number, ReturnType<typeof setTimeout>>>(
    new Map(),
  );

  // Fetch members once per project.
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    listMembers(slug)
      .then((m) => !cancelled && setMembers(m))
      .catch(() => !cancelled && setMembers([]));
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Centralised "is this task done?" predicate. Falls back to the
  // legacy is_completed flag when the project hasn't configured a
  // status catalog.
  const isDoneTask = useCallback(
    (t: TaskRead) =>
      taskStatuses.statuses.length > 0
        ? taskStatuses.isDone(t.status)
        : t.is_completed,
    [taskStatuses],
  );

  // Server params carry only what narrows the dataset at the source:
  // scope (assigned_to=me) and per-company narrowing. Priority,
  // completion, due-date filters and KPI computation all run on the
  // merged set client-side so the KPI cards and the quick-filter
  // pills always read from the same source of truth.
  const buildParams = useCallback((): TaskListParams => {
    const params: TaskListParams = {
      page_size: 500,
    };
    if (scope === "my_tasks") params.assigned_to = "me";
    if (companyIdFilter) params.company_id = companyIdFilter;
    return params;
  }, [scope, companyIdFilter]);

  // Only the latest request fills the list. The page loads its initial
  // scope before the saved (or remembered) view applies; the cross-project
  // My Tasks feed, the slower of the two, used to land last and replace
  // the Project Tasks list.
  const loadSeq = useRef(0);
  const load = useCallback(async () => {
    if (!slug) return;
    const seq = ++loadSeq.current;
    setStatus("loading");
    setErrorMessage(null);
    try {
      // "My tasks" pulls the cross-project feed — every project the user
      // belongs to, each row stamped with its project_slug / name. "All"
      // stays scoped to the current project. A company filter (deep-link
      // from a company page) is inherently project-scoped, so it forces
      // the per-project endpoint even under "My tasks".
      let items: TaskRead[];
      if (scope === "my_tasks" && !companyIdFilter) {
        // Cross-project personal pile, all pages merged (the KPIs /
        // pills read the full set client-side).
        items = await getAllMyTasks();
      } else {
        items = (await listTasks(slug, buildParams())).items;
      }
      if (seq !== loadSeq.current) return;
      setTasks(items);
      setStatus("ok");
    } catch (err) {
      if (seq !== loadSeq.current) return;
      setErrorMessage(extractMessage(err));
      setStatus("error");
    }
  }, [slug, scope, companyIdFilter, buildParams]);

  // The project a given task lives in. Cross-project rows (the My Tasks
  // feed) carry their own project_slug; per-project rows fall back to the
  // page slug. Every per-task mutation routes through this so a task in
  // another project still hits the right endpoint.
  const slugFor = useCallback(
    (task: TaskRead): string =>
      (task as { project_slug?: string }).project_slug ?? slug ?? "",
    [slug],
  );

  // Whether the loaded set spans more than one project (only possible
  // under "My tasks"). Gates the per-row project badge so single-project
  // setups — and the "All" scope — stay badge-free.
  const multiProject = useMemo(
    () =>
      new Set(
        tasks.map((t) => (t as { project_slug?: string }).project_slug ?? ""),
      ).size > 1,
    [tasks],
  );

  // Load the edited task's project members when it lives in another
  // project, so the assignee picker shows the right people.
  useEffect(() => {
    const editSlug = editTask ? slugFor(editTask) : null;
    if (!editSlug || editSlug === slug) {
      setEditMembers([]);
      return;
    }
    let cancelled = false;
    listMembers(editSlug)
      .then((m) => !cancelled && setEditMembers(m))
      .catch(() => !cancelled && setEditMembers([]));
    return () => {
      cancelled = true;
    };
  }, [editTask, slug, slugFor]);

  useEffect(() => {
    void load();
  }, [load]);

  // Per-pill counts. Independent of the priority Select / multi-status
  // filter / scope toggle so each pill always shows the same badge no
  // matter which other filters are on; counts therefore exclude done
  // tasks for every temporal pill (and the Completed pill counts the
  // done set itself).
  const pillCounts = useMemo(() => {
    const today = new Date();
    const todayStart = startOfDay(today);
    const weekEnd = endOfWeek(today, { weekStartsOn: 1 });
    let overdue = 0;
    let dueToday = 0;
    let dueThisWeek = 0;
    let noDueDate = 0;
    let completed = 0;
    for (const t of tasks) {
      if (isDoneTask(t)) {
        completed++;
        continue;
      }
      if (!t.due_at) {
        noDueDate++;
        continue;
      }
      const d = new Date(t.due_at);
      if (d < todayStart) overdue++;
      else if (isSameDay(d, today)) dueToday++;
      if (d >= todayStart && d <= weekEnd) dueThisWeek++;
    }
    return { overdue, dueToday, dueThisWeek, noDueDate, completed };
  }, [tasks, isDoneTask]);

  // KPIs are derived from the full per-scope task set so the cards
  // and the Completed filter pull from the same source of truth. They
  // intentionally do NOT honour the priority Select or the multi-
  // select status filter — the cards represent the whole scope so the
  // user can use them as launch pads back into specific quick filters.
  const summary = useMemo<TaskSummary>(() => {
    const today = new Date();
    const todayStart = startOfDay(today);
    const weekStart = startOfWeek(today, { weekStartsOn: 1 });
    let pending = 0;
    let overdue = 0;
    let dueToday = 0;
    let completedThisWeek = 0;
    for (const t of tasks) {
      if (isDoneTask(t)) {
        if (t.completed_at && new Date(t.completed_at) >= weekStart) {
          completedThisWeek++;
        }
        continue;
      }
      pending++;
      if (!t.due_at) continue;
      const d = new Date(t.due_at);
      if (d < todayStart) overdue++;
      else if (isSameDay(d, today)) dueToday++;
    }
    return {
      total: tasks.length,
      pending,
      overdue,
      due_today: dueToday,
      completed_this_week: completedThisWeek,
    };
  }, [tasks, isDoneTask]);

  /** Apply the priority Select + the status filter (both tabs) + the
   *  assignee filter (Project Tasks only — its control is hidden in My
   *  Tasks, so a stale selection must not silently filter the pile). */
  const applyClassicFilters = useCallback(
    (arr: TaskRead[]): TaskRead[] => {
      let result = arr;
      if (priority !== "all") {
        result = result.filter((t) => t.priority === priority);
      }
      if (statusFilter.length > 0) {
        const allow = new Set(statusFilter);
        result = result.filter((t) => !!t.status && allow.has(t.status));
      }
      if (scope === "all" && assigneeFilter !== "all") {
        result = result.filter((t) => t.assigned_to_id === assigneeFilter);
      }
      return result;
    },
    [priority, statusFilter, scope, assigneeFilter],
  );

  // List filter — exactly one of the pills is active at a time, so a
  // switch over `activeQuickFilter` is the cleanest read. The `null`
  // case is the implicit "default": hide done tasks so the user sees
  // only their open pile.
  const filteredForList = useMemo(() => {
    let result = applyClassicFilters(tasks);
    const today = new Date();
    const todayStart = startOfDay(today);
    const weekEnd = endOfWeek(today, { weekStartsOn: 1 });
    switch (activeQuickFilter) {
      case "overdue":
        result = result.filter(
          (t) =>
            !!t.due_at &&
            new Date(t.due_at) < todayStart &&
            !isDoneTask(t),
        );
        break;
      case "dueToday":
        result = result.filter(
          (t) =>
            !!t.due_at &&
            isSameDay(new Date(t.due_at), today) &&
            !isDoneTask(t),
        );
        break;
      case "dueThisWeek":
        result = result.filter((t) => {
          if (!t.due_at || isDoneTask(t)) return false;
          const d = new Date(t.due_at);
          return d >= todayStart && d <= weekEnd;
        });
        break;
      case "noDueDate":
        result = result.filter((t) => !t.due_at && !isDoneTask(t));
        break;
      case "completed":
        result = result.filter((t) => isDoneTask(t));
        break;
      default:
        result = result.filter((t) => !isDoneTask(t));
        break;
    }
    return result;
  }, [tasks, applyClassicFilters, activeQuickFilter, isDoneTask]);

  // Kanban filter — done tasks are *never* removed by temporal pills;
  // they always sit in their done column. The temporal pills filter
  // only the non-done cards, so empty non-done columns stay visible.
  // The "Completed" pill in kanban is enforced via column-hiding (see
  // kanbanVisibleStatusKeys below), not card filtering.
  const filteredForKanban = useMemo(() => {
    const result = applyClassicFilters(tasks);
    const today = new Date();
    const todayStart = startOfDay(today);
    const weekEnd = endOfWeek(today, { weekStartsOn: 1 });
    return result.filter((t) => {
      if (isDoneTask(t)) return true;
      switch (activeQuickFilter) {
        case "overdue":
          return !!t.due_at && new Date(t.due_at) < todayStart;
        case "dueToday":
          return !!t.due_at && isSameDay(new Date(t.due_at), today);
        case "dueThisWeek": {
          if (!t.due_at) return false;
          const d = new Date(t.due_at);
          return d >= todayStart && d <= weekEnd;
        }
        case "noDueDate":
          return !t.due_at;
        case "completed":
          // Non-done cards are hidden; the column-hiding logic
          // (kanbanVisibleStatusKeys) restricts the board to done
          // columns only.
          return false;
        default:
          return true;
      }
    });
  }, [tasks, applyClassicFilters, activeQuickFilter, isDoneTask]);

  const sortedListTasks = useMemo(() => {
    const arr = filteredForList.slice();
    if (activeQuickFilter === "completed") {
      arr.sort((a, b) => {
        const av = a.completed_at ?? "";
        const bv = b.completed_at ?? "";
        return bv.localeCompare(av);
      });
    } else {
      arr.sort((a, b) => {
        const ap = PRIORITY_RANK[a.priority];
        const bp = PRIORITY_RANK[b.priority];
        if (ap !== bp) return bp - ap;
        const ad = a.due_at ?? "9999-12-31";
        const bd = b.due_at ?? "9999-12-31";
        return ad.localeCompare(bd);
      });
    }
    return arr;
  }, [filteredForList, activeQuickFilter]);

  // Timeline shows the whole (classic-filtered) set — including done tasks
  // and every status — laid out by date. The date window, not the quick
  // pills, scopes it temporally, so it ignores activeQuickFilter.
  const timelineTasks = useMemo(
    () => applyClassicFilters(tasks),
    [applyClassicFilters, tasks],
  );

  function targetForToggle(currentlyDone: boolean):
    | { kind: "status"; key: string }
    | { kind: "boolean" }
    | null {
    if (taskStatuses.statuses.length === 0) return { kind: "boolean" };
    if (currentlyDone) {
      const target =
        taskStatuses.initialKey ??
        taskStatuses.statuses.find((s) => !s.is_done)?.key;
      if (!target) return null;
      return { kind: "status", key: target };
    }
    const target = taskStatuses.statuses.find((s) => s.is_done)?.key;
    if (!target) return null;
    return { kind: "status", key: target };
  }

  function clearFade(taskId: number) {
    const t = fadeTimers.current.get(taskId);
    if (t) {
      clearTimeout(t);
      fadeTimers.current.delete(taskId);
    }
    setFadingIds((prev) => {
      if (!prev.has(taskId)) return prev;
      const next = new Set(prev);
      next.delete(taskId);
      return next;
    });
  }

  async function handleToggle(task: TaskRead) {
    if (!slug) return;
    const wasDone = isDoneTask(task);
    const target = targetForToggle(wasDone);
    if (!target) {
      toast.error(
        "Task statuses misconfigured, set an 'is_done' status in Settings.",
      );
      return;
    }
    const nowIso = new Date().toISOString();
    const optimistic: TaskRead =
      target.kind === "status"
        ? {
            ...task,
            status: target.key,
            is_completed: !wasDone,
            completed_at: !wasDone ? nowIso : null,
          }
        : {
            ...task,
            is_completed: !wasDone,
            completed_at: !wasDone ? nowIso : null,
          };
    setTasks((prev) =>
      prev.map((t) => (t.id === task.id ? optimistic : t)),
    );

    // Fade out the row only when completing while the list view will
    // hide done tasks (i.e., the default state with no quick filter).
    // The kanban never relies on this — done cards stay rendered.
    const willFadeOut =
      !wasDone && viewMode === "list" && activeQuickFilter === null;
    if (willFadeOut) {
      setFadingIds((prev) => new Set(prev).add(task.id));
      const timer = setTimeout(() => {
        setTasks((prev) => prev.filter((t) => t.id !== task.id));
        setFadingIds((prev) => {
          const next = new Set(prev);
          next.delete(task.id);
          return next;
        });
        fadeTimers.current.delete(task.id);
      }, 800);
      fadeTimers.current.set(task.id, timer);
    }

    try {
      const taskSlug = slugFor(task);
      const updated =
        target.kind === "status"
          ? await updateTask(taskSlug, task.id, { status: target.key })
          : !wasDone
            ? await completeTask(taskSlug, task.id)
            : await reopenTask(taskSlug, task.id);
      setTasks((prev) =>
        prev.map((t) => (t.id === task.id ? updated : t)),
      );
      if (!wasDone) {
        const previousStatus = task.status ?? null;
        toast.success("Task completed", {
          duration: 5000,
          action: {
            label: "Undo",
            onClick: () => {
              clearFade(task.id);
              void undoToggle(task, previousStatus);
            },
          },
        });
      }
    } catch (err) {
      clearFade(task.id);
      setTasks((prev) =>
        prev.map((t) => (t.id === task.id ? task : t)),
      );
      toast.error(extractMessage(err));
    }
  }

  async function undoToggle(
    task: TaskRead,
    previousStatus: string | null,
  ) {
    if (!slug) return;
    try {
      const useDynamic = taskStatuses.statuses.length > 0;
      const taskSlug = slugFor(task);
      const reverted =
        useDynamic && previousStatus
          ? await updateTask(taskSlug, task.id, { status: previousStatus })
          : await reopenTask(taskSlug, task.id);
      setTasks((prev) => {
        if (prev.some((t) => t.id === task.id)) {
          return prev.map((t) => (t.id === task.id ? reverted : t));
        }
        return [reverted, ...prev];
      });
    } catch {
      toast.error("Couldn't undo.");
    }
  }

  async function handleDeleteConfirmed() {
    if (!slug || !deleteTarget) return;
    setDeleting(true);
    try {
      await deleteTask(slugFor(deleteTarget), deleteTarget.id);
      setTasks((prev) => prev.filter((t) => t.id !== deleteTarget.id));
      toast.success("Task deleted");
      setDeleteTarget(null);
    } catch (err) {
      toast.error(extractMessage(err));
    } finally {
      setDeleting(false);
    }
  }

  function handleSaved(task: TaskRead) {
    setTasks((prev) => {
      const idx = prev.findIndex((t) => t.id === task.id);
      if (idx >= 0) {
        const next = prev.slice();
        next[idx] = task;
        return next;
      }
      return [task, ...prev];
    });
    void load();
  }

  function clearCompanyFilter() {
    const next = new URLSearchParams(searchParams);
    next.delete("company_id");
    setSearchParams(next, { replace: true });
  }

  // KPI cards mirror the active pill 1:1 because the pills are now
  // mutually exclusive. "Pending" lights up when no pill is active —
  // it's the "no temporal lens" catch-all state.
  const activeKpiKeys = useMemo<ReadonlySet<SummaryKey>>(() => {
    const set = new Set<SummaryKey>();
    if (activeQuickFilter === "overdue") set.add("overdue");
    else if (activeQuickFilter === "dueToday") set.add("due_today");
    else if (activeQuickFilter === "completed") set.add("completed");
    if (set.size === 0) set.add("pending");
    return set;
  }, [activeQuickFilter]);

  function handleKpiSelect(key: SummaryKey) {
    if (key === "pending") {
      setActiveQuickFilter(null);
      return;
    }
    if (key === "overdue") toggleQuickFilter("overdue");
    else if (key === "due_today") toggleQuickFilter("dueToday");
    else if (key === "completed") toggleQuickFilter("completed");
  }

  // The first configured `is_done` status drives the visual identity
  // of "completion" on the page: the round checkbox in the list view,
  // the Completed quick-filter pill, and the dimmed cards in the
  // matching kanban column.
  const firstDoneStatus = useMemo(
    () => taskStatuses.statuses.find((s) => s.is_done) ?? null,
    [taskStatuses.statuses],
  );
  const donePalette = useMemo(
    () => paletteFor(firstDoneStatus?.color),
    [firstDoneStatus],
  );
  const completedPillTone = useMemo<QuickPillTone>(
    () => pillToneForColor(firstDoneStatus?.color),
    [firstDoneStatus],
  );

  // Which Kanban columns render. The status multi-select is the single
  // control: unchecked statuses hide their column (and filter their cards
  // out — see applyClassicFilters). The "Completed" pill restricts the
  // board to the done columns. Empty filter → all columns.
  const kanbanVisibleStatusKeys = useMemo<string[] | undefined>(() => {
    if (activeQuickFilter === "completed") {
      return Array.from(taskStatuses.doneKeys);
    }
    if (statusFilter.length > 0) return statusFilter;
    return undefined;
  }, [activeQuickFilter, statusFilter, taskStatuses.doneKeys]);

  // The live filter blob — what a saved view stores.
  const currentFilters = useMemo<TaskViewFilters>(
    () => ({
      tab: scope,
      view_mode: viewMode,
      // "all members" → empty array (no narrowing); a specific member id
      // otherwise. Array shape keeps the door open for multi-assignee.
      filter_user_ids: assigneeFilter === "all" ? [] : [assigneeFilter],
      filter_statuses: statusFilter,
    }),
    [scope, viewMode, assigneeFilter, statusFilter],
  );

  // Whether the active view's stored filters differ from the live ones
  // (order-insensitive on the array fields).
  const hasUnsavedChanges = useMemo(() => {
    if (!activeView) return false;
    const norm = (f: TaskViewFilters) =>
      JSON.stringify({
        tab: f.tab ?? "my_tasks",
        view_mode: f.view_mode ?? "list",
        // "all" sentinel and empty array both mean "no assignee filter".
        filter_user_ids: [...(f.filter_user_ids ?? [])]
          .filter((x) => x !== "all")
          .sort(),
        filter_statuses: [...(f.filter_statuses ?? [])].sort(),
      });
    return norm(currentFilters) !== norm(activeView.filters as TaskViewFilters);
  }, [activeView, currentFilters]);

  const canManageViews = perms.canCreateViews;

  async function handleCreateView(name: string) {
    const view = await savedViews.create(
      name,
      currentFilters as Record<string, unknown>,
    );
    setActiveView(view);
    toast.success(`View “${name}” created`);
  }
  async function handleSaveActiveView() {
    if (!activeView) return;
    const view = await savedViews.update(activeView.id, {
      filters: currentFilters as Record<string, unknown>,
    });
    setActiveView(view);
    toast.success("View updated");
  }
  // Rename keeps its toast (ViewsDropdown doesn't toast rename success).
  async function handleRenameView(id: number, name: string) {
    const view = await savedViews.update(id, { name });
    if (activeView?.id === id) setActiveView(view);
    toast.success("View renamed");
  }
  // Delete + default toggle are surfaced through ViewsDropdown, which owns
  // their success toasts — these just do the work and sync activeView.
  async function handleDeleteView(id: number) {
    await savedViews.remove(id);
    if (activeView?.id === id) setActiveView(null);
  }
  async function handleToggleDefaultView(view: SavedViewRead) {
    const updated = view.is_default
      ? await savedViews.unsetDefault(view.id)
      : await savedViews.setDefault(view.id);
    if (activeView?.id === view.id) setActiveView(updated);
  }

  // Clear all active filters back to a clean ad-hoc state (keeps the
  // current tab + view mode; deselects any named view).
  const hasActiveFilters =
    priority !== "all" ||
    statusFilter.length > 0 ||
    (scope === "all" && assigneeFilter !== "all") ||
    activeQuickFilter !== null ||
    activeView !== null;
  function handleClearAll() {
    setPriority("all");
    setStatusFilter([]);
    setAssigneeFilter("all");
    setActiveQuickFilter(null);
    setActiveView(null);
  }

  if (!perms.canViewTasks || !project) return null;

  // My Tasks + Kanban swaps the single cross-project board for project
  // sub-tabs (one board per project, each with its own status columns),
  // so the page's cross-project KPIs / pills / filters are hidden in
  // that mode — they don't map onto a single project's board.
  const isMyTasksKanban =
    scope === "my_tasks" && viewMode === "kanban" && kanbanEnabled;

  return (
    <div className="space-y-6 pb-12">
      <header className="flex items-end justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
              Tasks
            </h1>
            {summary && (
              <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">
                {formatNumber(summary.pending)} pending
              </span>
            )}
          </div>
          <p className="text-sm text-slate-500">{project.name}</p>
        </div>
        <div className="flex items-center gap-2">
          {kanbanEnabled && (
            <div
              role="group"
              aria-label="View"
              className="inline-flex h-9 rounded-lg border border-slate-200 bg-slate-50 p-0.5"
            >
              <button
                type="button"
                onClick={() => setViewMode("list")}
                aria-pressed={viewMode === "list"}
                className={cn(
                  "inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-xs font-medium transition-colors",
                  viewMode === "list"
                    ? "bg-white text-slate-900 shadow-sm-tile"
                    : "text-slate-500 hover:text-slate-700",
                )}
              >
                <List className="h-3.5 w-3.5" />
                List
              </button>
              <button
                type="button"
                onClick={() => setViewMode("kanban")}
                aria-pressed={viewMode === "kanban"}
                className={cn(
                  "inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-xs font-medium transition-colors",
                  viewMode === "kanban"
                    ? "bg-white text-slate-900 shadow-sm-tile"
                    : "text-slate-500 hover:text-slate-700",
                )}
              >
                <KanbanSquare className="h-3.5 w-3.5" />
                Kanban
              </button>
              <button
                type="button"
                onClick={() => setViewMode("timeline")}
                aria-pressed={viewMode === "timeline"}
                className={cn(
                  "inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-xs font-medium transition-colors",
                  viewMode === "timeline"
                    ? "bg-white text-slate-900 shadow-sm-tile"
                    : "text-slate-500 hover:text-slate-700",
                )}
              >
                <GanttChartSquare className="h-3.5 w-3.5" />
                Timeline
              </button>
            </div>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={status === "loading"}
          >
            <RefreshCcw
              className={
                status === "loading"
                  ? "mr-2 h-3.5 w-3.5 animate-spin"
                  : "mr-2 h-3.5 w-3.5"
              }
            />
            Refresh
          </Button>
          {perms.canCreateTasks && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setCreateOpen(true)}
            >
              <Plus className="mr-2 h-3.5 w-3.5" />
              New task
            </Button>
          )}
        </div>
      </header>

      {!isMyTasksKanban && (
        <TaskSummaryCards
          summary={summary}
          loading={status === "loading"}
          activeKeys={activeKpiKeys}
          onSelect={handleKpiSelect}
        />
      )}

      {/* Filter row — left: My Tasks / Project Tasks tabs. Right: the
       *  combinable temporal pills. The two zones are visually
       *  separated by a 6 unit gap (≈ 24 px) so the user reads them
       *  as orthogonal controls.
       *
       *  "My Tasks" = the cross-project personal feed; "Project Tasks" =
       *  the current project's full board (all users). The List / Kanban
       *  toggle in the header applies to both. */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <div
          role="group"
          aria-label="Tasks scope"
          className="inline-flex h-9 rounded-lg border border-slate-200 bg-slate-50 p-0.5"
        >
          <ScopeButton
            active={scope === "my_tasks"}
            label="My Tasks"
            onClick={() => setScopeManual("my_tasks")}
          />
          <ScopeButton
            active={scope === "all"}
            label="Project Tasks"
            onClick={() => setScopeManual("all")}
          />
        </div>
        {!isMyTasksKanban && viewMode !== "timeline" && (
        <div className="flex flex-wrap items-center gap-2">
          <QuickPill
            active={activeQuickFilter === "overdue"}
            tone="rose"
            onClick={() => toggleQuickFilter("overdue")}
            label="Overdue"
            badge={pillCounts.overdue > 0 ? pillCounts.overdue : undefined}
          />
          <QuickPill
            active={activeQuickFilter === "dueToday"}
            tone="amber"
            onClick={() => toggleQuickFilter("dueToday")}
            label="Due today"
            badge={pillCounts.dueToday > 0 ? pillCounts.dueToday : undefined}
          />
          <QuickPill
            active={activeQuickFilter === "dueThisWeek"}
            tone="blue"
            onClick={() => toggleQuickFilter("dueThisWeek")}
            label="Due this week"
            badge={
              pillCounts.dueThisWeek > 0 ? pillCounts.dueThisWeek : undefined
            }
          />
          <QuickPill
            active={activeQuickFilter === "noDueDate"}
            tone="slate"
            onClick={() => toggleQuickFilter("noDueDate")}
            label="No due date"
            badge={
              pillCounts.noDueDate > 0 ? pillCounts.noDueDate : undefined
            }
          />
          <QuickPill
            active={activeQuickFilter === "completed"}
            tone={completedPillTone}
            onClick={() => toggleQuickFilter("completed")}
            label="Completed"
            badge={
              pillCounts.completed > 0 ? pillCounts.completed : undefined
            }
          />
        </div>
        )}
      </div>

      {/* Saved views bar — Project Tasks only. My Tasks is the cross-project
       *  personal pile, not a place for custom per-project views. */}
      {scope === "all" && (savedViews.views.length > 0 || canManageViews) && (
        <TaskViewSelector
          views={savedViews.views}
          loading={savedViews.loading}
          activeView={activeView}
          onSelect={applyView}
          onCreate={handleCreateView}
          onRename={handleRenameView}
          onDelete={handleDeleteView}
          onToggleDefault={handleToggleDefaultView}
          onSaveActive={handleSaveActiveView}
          onReset={() => activeView && applyView(activeView)}
          hasUnsavedChanges={hasUnsavedChanges}
          canManage={canManageViews}
        />
      )}

      {/* Classic filters row */}
      {!isMyTasksKanban && (
      <div className="flex flex-wrap items-center gap-3 rounded-plane bg-white p-3 shadow-plane">
        <Select
          value={priority}
          onValueChange={(v) => setPriority(v as TaskPriority | "all")}
        >
          <SelectTrigger
            className="h-9 w-[160px] rounded-lg border-slate-200 bg-white"
            aria-label="Priority"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All priorities</SelectItem>
            <SelectItem value="urgent">Urgent</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>

        {/* Status filter — both tabs. In Project Tasks Kanban it also hides
         *  the unchecked columns; in lists it filters rows. */}
        {taskStatuses.statuses.length > 0 && (
          <StatusFilterPopover
            statuses={taskStatuses.statuses}
            selected={statusFilter}
            onChange={setStatusFilter}
          />
        )}

        {/* Assignee filter — Project Tasks only (My Tasks is already the
         *  current user's pile). */}
        {scope === "all" && (
          <AssigneeFilter
            members={members}
            value={assigneeFilter}
            onChange={setAssigneeFilter}
          />
        )}

        {hasActiveFilters && (
          <button
            type="button"
            onClick={handleClearAll}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg px-2.5 text-sm font-medium text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700"
          >
            <X className="h-3.5 w-3.5" />
            Clear all
          </button>
        )}

        {companyIdFilter && (
          <button
            type="button"
            onClick={clearCompanyFilter}
            className="ml-auto rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-0.5 text-xs font-medium text-indigo-700 hover:bg-indigo-100"
          >
            Filtered to one company · Clear
          </button>
        )}
      </div>
      )}

      {!isMyTasksKanban && status === "error" && (
        <ErrorBanner message={errorMessage} onRetry={load} />
      )}

      {status === "loading" && viewMode === "list" ? (
        <TaskListSkeleton rows={6} />
      ) : viewMode === "timeline" ? (
        <div>
          <TimelineNavigation
            startDate={timelineStart}
            endDate={timelineEnd}
            onRangeChange={(s, e) => {
              setTimelineStart(s);
              setTimelineEnd(e);
            }}
          />
          <TimelineView
            tasks={timelineTasks}
            startDate={timelineStart}
            endDate={timelineEnd}
            showProject={scope === "my_tasks" && multiProject}
            getStatusConfig={taskStatuses.getStatusConfig}
            onOpenDetail={(t) => setDetailTask(t)}
          />
        </div>
      ) : viewMode === "kanban" ? (
        isMyTasksKanban ? (
          <MyTasksKanban defaultSlug={slug ?? undefined} />
        ) : (
        <TaskKanbanBoard
          tasks={filteredForKanban}
          projectSlug={slug ?? ""}
          canEdit={perms.canEditTasks}
          canDelete={perms.canDeleteTasks}
          loading={status === "loading"}
          statuses={taskStatuses.statuses}
          visibleStatusKeys={kanbanVisibleStatusKeys}
          // Lift the soft 10-card cap on done columns when the user
          // explicitly asked to focus on completed work.
          expandDoneColumns={activeQuickFilter === "completed"}
          onTaskUpdated={(updated) => {
            setTasks((prev) =>
              prev.map((t) => (t.id === updated.id ? updated : t)),
            );
          }}
          onOpenDetail={(t) => setDetailTask(t)}
          onAskDelete={
            perms.canDeleteTasks
              ? (t) => setDeleteTarget(t)
              : undefined
          }
          onAddTask={(col) => {
            setCreateDefaultStatus(
              col.apply.kind === "status" ? col.apply.statusKey : null,
            );
            setCreateOpen(true);
          }}
        />
        )
      ) : (
        <TaskList
          tasks={sortedListTasks}
          projectSlug={slug ?? ""}
          showProjectBadge={scope === "my_tasks" && multiProject}
          canEdit={perms.canEditTasks}
          canDelete={perms.canDeleteTasks}
          onToggleComplete={handleToggle}
          onEdit={
            perms.canEditTasks ? (t) => setEditTask(t) : undefined
          }
          onDelete={
            perms.canDeleteTasks ? (t) => setDeleteTarget(t) : undefined
          }
          onOpenDetail={(t) => setDetailTask(t)}
          fadingIds={fadingIds}
          emptyTitle={emptyTitleFor(activeQuickFilter, scope)}
          emptyHint={emptyHintFor(activeQuickFilter)}
          getStatusConfig={taskStatuses.getStatusConfig}
          donePalette={donePalette}
        />
      )}

      {slug && (
        <TaskDetailSheet
          task={detailTask}
          open={!!detailTask}
          onOpenChange={(o) => {
            if (!o) setDetailTask(null);
          }}
          projectSlug={detailTask ? slugFor(detailTask) : slug}
          onTaskUpdated={(updated) => {
            setTasks((prev) =>
              prev.map((t) => (t.id === updated.id ? updated : t)),
            );
            setDetailTask(updated);
          }}
          onTaskDeleted={(id) => {
            setTasks((prev) => prev.filter((t) => t.id !== id));
            setDetailTask(null);
          }}
          onAskEdit={
            perms.canEditTasks
              ? (t) => {
                  setDetailTask(null);
                  setEditTask(t);
                }
              : undefined
          }
        />
      )}

      {slug && (
        <TaskCreateDialog
          projectSlug={slug}
          open={createOpen}
          onOpenChange={(o) => {
            setCreateOpen(o);
            if (!o) setCreateDefaultStatus(null);
          }}
          members={members}
          defaultAssigneeId={currentUser?.id ?? ""}
          defaultStatus={
            createDefaultStatus ?? taskStatuses.initialKey ?? null
          }
          taskStatuses={taskStatuses.statuses}
          onSaved={handleSaved}
        />
      )}

      {slug && editTask && (
        <TaskCreateDialog
          mode="edit"
          task={editTask}
          projectSlug={slugFor(editTask)}
          open={!!editTask}
          onOpenChange={(o) => {
            if (!o) setEditTask(null);
          }}
          members={slugFor(editTask) === slug ? members : editMembers}
          onSaved={handleSaved}
        />
      )}

      <Dialog
        open={!!deleteTarget}
        onOpenChange={(o) => {
          if (deleting) return;
          if (!o) setDeleteTarget(null);
        }}
      >
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Delete task?</DialogTitle>
            <DialogDescription>
              {deleteTarget && (
                <>
                  This will permanently delete the task{" "}
                  <span className="font-medium text-slate-900">
                    &ldquo;{deleteTarget.title}&rdquo;
                  </span>
                  .
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-start gap-3 rounded-lg border border-rose-200 bg-rose-50/60 px-3 py-2.5">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" />
            <p className="text-xs leading-relaxed text-rose-700">
              This cannot be undone.
            </p>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setDeleteTarget(null)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => void handleDeleteConfirmed()}
              disabled={deleting}
            >
              {deleting && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface ScopeButtonProps {
  active: boolean;
  label: string;
  onClick: () => void;
}

function ScopeButton({ active, label, onClick }: ScopeButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "inline-flex h-8 items-center rounded-md px-3 text-xs font-medium transition-colors",
        active
          ? "bg-indigo-600 text-white shadow-sm-tile"
          : "text-slate-500 hover:text-slate-700",
      )}
    >
      {label}
    </button>
  );
}

type QuickPillTone =
  | "indigo"
  | "rose"
  | "amber"
  | "emerald"
  | "blue"
  | "slate"
  | "violet";

interface QuickPillProps {
  active: boolean;
  label: string;
  onClick: () => void;
  /** Tinted active state — defaults to indigo. */
  tone?: QuickPillTone;
  /** Optional count badge (rendered to the right of the label). */
  badge?: number;
}

/** Map a status-config color name to a QuickPill tone. The status
 *  catalog vocabulary is broader than the pill's tone enum (includes
 *  aliases like "green" / "yellow"), so we collapse them down to the
 *  closest tinted pill. Unknown colors fall back to emerald — the
 *  historical default for "completed". */
function pillToneForColor(
  color: string | null | undefined,
): QuickPillTone {
  switch (color) {
    case "rose":
    case "red":
      return "rose";
    case "amber":
    case "yellow":
      return "amber";
    case "emerald":
    case "green":
      return "emerald";
    case "blue":
      return "blue";
    case "indigo":
      return "indigo";
    case "violet":
    case "purple":
      return "violet";
    case "slate":
    case "gray":
      return "slate";
    default:
      return "emerald";
  }
}

function QuickPill({
  active,
  label,
  onClick,
  tone = "indigo",
  badge,
}: QuickPillProps) {
  const activeStyles: Record<QuickPillTone, string> = {
    indigo: "border-indigo-300 bg-indigo-50 text-indigo-700",
    rose: "border-rose-300 bg-rose-50 text-rose-700",
    amber: "border-amber-300 bg-amber-50 text-amber-700",
    emerald: "border-emerald-300 bg-emerald-50 text-emerald-700",
    blue: "border-blue-300 bg-blue-50 text-blue-700",
    slate: "border-slate-300 bg-slate-100 text-slate-700",
    violet: "border-violet-300 bg-violet-50 text-violet-700",
  };
  const badgeStyles: Record<QuickPillTone, string> = {
    indigo: "bg-indigo-100 text-indigo-700",
    rose: "bg-rose-100 text-rose-700",
    amber: "bg-amber-100 text-amber-700",
    emerald: "bg-emerald-100 text-emerald-700",
    blue: "bg-blue-100 text-blue-700",
    slate: "bg-slate-200 text-slate-700",
    violet: "bg-violet-100 text-violet-700",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-xs font-medium transition-colors",
        active
          ? activeStyles[tone]
          : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50",
      )}
    >
      {label}
      {badge !== undefined && (
        <span
          className={cn(
            "rounded-full px-1.5 py-0 text-[10px] font-semibold tabular-nums",
            active ? badgeStyles[tone] : "bg-slate-100 text-slate-600",
          )}
        >
          {badge}
        </span>
      )}
    </button>
  );
}

interface AssigneeFilterProps {
  members: ProjectMember[];
  value: string;
  onChange: (next: string) => void;
}

/** Project Tasks assignee narrowing — "All members" or a specific member. */
function AssigneeFilter({ members, value, onChange }: AssigneeFilterProps) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        className="h-9 w-[190px] rounded-lg border-slate-200 bg-white"
        aria-label="Assignee"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All members</SelectItem>
        {members.map((m) => (
          <SelectItem key={m.id} value={m.id}>
            {m.full_name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

interface StatusFilterPopoverProps {
  statuses: ReadonlyArray<{ key: string; label: string; color?: string }>;
  selected: string[];
  onChange: (next: string[]) => void;
}

function StatusFilterPopover({
  statuses,
  selected,
  onChange,
}: StatusFilterPopoverProps) {
  const triggerLabel =
    selected.length === 0
      ? "Status"
      : selected.length === 1
        ? statuses.find((s) => s.key === selected[0])?.label ?? "Status"
        : `${selected.length} statuses`;
  function toggle(key: string) {
    if (selected.includes(key)) {
      onChange(selected.filter((k) => k !== key));
    } else {
      onChange([...selected, key]);
    }
  }
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex h-9 items-center gap-2 rounded-lg border bg-white px-3 text-sm transition-colors",
            selected.length > 0
              ? "border-indigo-300 text-indigo-700"
              : "border-slate-200 text-slate-700 hover:border-slate-300",
          )}
        >
          {triggerLabel}
          {selected.length > 0 && (
            <span className="rounded-full bg-indigo-100 px-1.5 py-0 text-[10px] font-semibold text-indigo-700 tabular-nums">
              {selected.length}
            </span>
          )}
          <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-1">
        <div className="max-h-[280px] overflow-y-auto py-1">
          {statuses.map((s) => {
            const isOn = selected.includes(s.key);
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => toggle(s.key)}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm text-slate-700 transition-colors hover:bg-slate-50"
              >
                <span
                  className={cn(
                    "flex h-4 w-4 shrink-0 items-center justify-center rounded border",
                    isOn
                      ? "border-indigo-500 bg-indigo-500 text-white"
                      : "border-slate-300 bg-white",
                  )}
                >
                  {isOn && <Check className="h-3 w-3" strokeWidth={3} />}
                </span>
                <span className="truncate">{s.label}</span>
              </button>
            );
          })}
        </div>
        {selected.length > 0 && (
          <div className="border-t border-slate-900/[0.06] p-1">
            <button
              type="button"
              onClick={() => onChange([])}
              className="w-full rounded-md px-2 py-1.5 text-left text-xs font-medium text-slate-500 transition-colors hover:bg-slate-50"
            >
              Clear
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function emptyTitleFor(qf: QuickFilter, scope: Scope): string {
  switch (qf) {
    case "completed":
      return "No completed tasks";
    case "overdue":
      return "Nothing overdue";
    case "dueToday":
      return "Nothing due today";
    case "dueThisWeek":
      return "Nothing due this week";
    case "noDueDate":
      return "Every task has a due date";
    default:
      return scope === "my_tasks"
        ? "No tasks assigned to you"
        : "No tasks in this project";
  }
}

function emptyHintFor(qf: QuickFilter): string | undefined {
  if (qf === "overdue") return "Great, everything is on track.";
  if (qf === "completed") return undefined;
  return "Create a task to keep your pipeline moving.";
}

function ErrorBanner({
  message,
  onRetry,
}: {
  message: string | null;
  onRetry: () => void;
}) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
      <div>
        <p className="text-sm font-medium text-rose-700">
          Couldn&rsquo;t load tasks
        </p>
        {message && <p className="text-xs text-rose-600">{message}</p>}
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={onRetry}
        className="border-rose-200 bg-white text-rose-700 hover:bg-rose-100"
      >
        <RefreshCcw className="mr-2 h-3.5 w-3.5" />
        Retry
      </Button>
    </div>
  );
}

function extractMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    if (err.code === "ERR_NETWORK") return "Cannot reach the server.";
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
  }
  return "An unexpected error occurred.";
}
